import { IReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';
import { BooleanOperators, FilterOperators } from '@opengov/reporting-types/src/reportFilters';
import { ColumnTypes } from '../../../../src/components/table/columnTypes';

interface IMultiFilter {
  filters: Array<{ operator: string; text: string }>;
  booleanOperator: BooleanOperators;
}

import { getTableColumnTypeFromReportColumnType } from '../../../common/helpers/semanticTypeToColumnType';
import { reportConfigurationEffects } from '../../../common/reportConfiguration/effects/reportConfigurationEffects';
import { reportConfigurationActionCreators } from '../../../common/reportConfiguration/state';
import { monitor } from '../../../performance';

export const startMeasurementAfterApplyingFilters = () => {
  const updateFiltersMeasurementObject = {
    measureName: 'charts-details-update-filter-load',
    spinnerWaitOptions: {
      shouldDetect: true,
      selector: '[data-test="data-table-updating-indicator"]'
    },
    loadingBlocksWaitOptions: { shouldDetect: false }
  };
  void monitor.measureUntilDoneLoading(updateFiltersMeasurementObject);
};

export const applyTextFilter = (value, columnName) => {
  reportConfigurationActionCreators.setColumnFilters([
    {
      operator: FilterOperators.Contain,
      value,
      columnName
    }
  ]);
  reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
};

export const applyEmptyValuesFilter = (columnName: string) => {
  reportConfigurationActionCreators.setColumnFilters([
    {
      operator: FilterOperators.Blank,
      value: null,
      columnName
    }
  ]);
  reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
};

export const applyTextMultiFilter = (multiFilter: IMultiFilter, columnName: string) => {
  const processedFilters = multiFilter.filters.map((filter) => {
    return { columnName: columnName, operator: filter.operator as any, value: filter.text };
  });

  reportConfigurationActionCreators.setColumnFilters(processedFilters);
  reportConfigurationActionCreators.setColumnFilterBooleanOperators([
    { booleanOperator: multiFilter.booleanOperator, columnName: columnName }
  ]);
  reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
  startMeasurementAfterApplyingFilters();
};

export const applyBooleanFilter = (columnName: string, value: string) => {
  const filters = [{ columnName: columnName, operator: FilterOperators.Equal, value }];

  reportConfigurationActionCreators.setColumnFilters(filters as any);
  reportConfigurationActionCreators.setColumnFilterBooleanOperators([
    { booleanOperator: BooleanOperators.And, columnName: columnName }
  ]);
  reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
};

export const applyNumberFilter = (minValue, maxValue, columnName) => {
  const numberRangeFilter = [];
  const hasMinValue = minValue !== null && typeof minValue === 'number';
  const hasMaxValue = maxValue !== null && typeof maxValue === 'number';

  if (hasMinValue && hasMaxValue && minValue === maxValue) {
    numberRangeFilter.push({
      operator: FilterOperators.Equal,
      value: minValue,
      columnName: columnName
    });

    reportConfigurationActionCreators.setColumnFilters(numberRangeFilter);
    reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
    startMeasurementAfterApplyingFilters();
    return;
  }

  if (hasMinValue) {
    numberRangeFilter.push({
      operator: FilterOperators.Greater,
      value: minValue,
      columnName: columnName
    });
  }

  if (hasMaxValue) {
    numberRangeFilter.push({
      operator: FilterOperators.Lesser,
      value: maxValue,
      columnName: columnName
    });
  }

  reportConfigurationActionCreators.setColumnFilters(numberRangeFilter);
  reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
  startMeasurementAfterApplyingFilters();
};

export const applyDateFilter = (minValue, maxValue, columnName) => {
  const dateRangeFilter = [];

  if (minValue && maxValue && minValue === maxValue) {
    dateRangeFilter.push({
      operator: FilterOperators.Equal,
      value: minValue,
      columnName: columnName
    });

    reportConfigurationActionCreators.setColumnFilters(dateRangeFilter);
    reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
    startMeasurementAfterApplyingFilters();
    return;
  }

  if (minValue) {
    dateRangeFilter.push({
      operator: FilterOperators.Greater,
      value: minValue,
      columnName: columnName
    });
  }

  if (maxValue) {
    dateRangeFilter.push({
      operator: FilterOperators.Lesser,
      value: maxValue,
      columnName: columnName
    });
  }

  reportConfigurationActionCreators.setColumnFilters(dateRangeFilter);
  reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
  startMeasurementAfterApplyingFilters();
};

// `state` is actually `any`
export const applyFilter = (column: IReportColumn, state: any) => {
  const columnType = getTableColumnTypeFromReportColumnType(column);

  switch (columnType) {
    case ColumnTypes.Boolean:
    case ColumnTypes.Text:
      applyTextMultiFilter(state, column.name);
      return;
    case ColumnTypes.Year:
    case ColumnTypes.Date: //End this switch case with an unconditional break, continue, return or throw statement
      applyDateFilter(state.minValue, state.maxValue, column.name);
      return;
    case ColumnTypes.Period:
    case ColumnTypes.Percent:
    case ColumnTypes.Currency:
    case ColumnTypes.Number: //End this switch case with an unconditional break, continue, return or throw statement
      applyNumberFilter(state.minValue, state.maxValue, column.name);
      return;
    default:
      return;
  }
};
